import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { format, parse } from 'date-fns';
import ISO_639_LANGUAGES from '../data/iso639Languages';
import ISO_3166_COUNTRIES from '../data/iso3166Countries';
import { CalendarIcon, Plus, X, Loader2, Save, Upload, Trash2, FolderOpen, FolderCheck, FolderX } from 'lucide-react';
import { CAPABILITY_OPTIONS, S1000D_VERSIONS, SECURITY_CLASSIFICATIONS } from '../constants/projectOptions';
import { CURRENCIES, findCurrency } from '../pages/estimator/lib/s1000dData';
import { isFileSystemAccessSupported, pickDirectory, saveDirectoryHandle, getDirectoryHandle, removeDirectoryHandle, verifyPermission } from '../utils/workingDirectory';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectFormModal = ({ open, onOpenChange, project, onSuccess }) => {
  const { token } = useAuth();
  const isEditing = !!project;
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    s1000d_version: 'Issue 5.0',
    description: '',
    capability: '00 - Product - General',
    model_ident_codes: [],
    system_difference_code: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    delivery_dates: [],
    responsible_partner_companies: [],
    originators: [],
    applicability: '',
    security_classification: '01 - Unclassified',
    brex_modules: {
      project: '',
      department: '',
      division: '',
      organisation: '',
      s1000d_default: ''
    },
    country_iso_code: 'AU',
    language_iso_code: 'en',
    working_directory: '',
    ste_technical_nouns: [],
    analytics: {
      start_date: '',
      end_date: '',
      currency: 'AUD',
      total_budget: 0,
      target_total_dms: 0,
    },
    roles_rates: [],
  });

  // Working directory handle (stored in IndexedDB, not sent to server)
  const [dirHandle, setDirHandle] = useState(null);
  const [dirStatus, setDirStatus] = useState('none'); // 'none' | 'linked' | 'error'

  // Temp state for adding items
  const [newMIC, setNewMIC] = useState('');
  const [newDeliveryDate, setNewDeliveryDate] = useState(null);
  const [newDeliveryLabel, setNewDeliveryLabel] = useState('');
  const [newRPC, setNewRPC] = useState({ company_name: '', cage_code: '' });
  const [newOriginator, setNewOriginator] = useState({ company_name: '', cage_code: '' });
  const [newNoun, setNewNoun] = useState({ word: '', approved_meaning: '', example: '', non_ste_example: '' });
  const [budgetInput, setBudgetInput] = useState(null);

  const fmtMoney = (v, code) =>
    `${findCurrency(code).symbol}${Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const [steImporting, setSteImporting] = useState(false);

  // Load project data when editing
  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        s1000d_version: project.s1000d_version || 'Issue 5.0',
        description: project.description || '',
        capability: project.capability || '00 - Product - General',
        model_ident_codes: project.model_ident_codes || [],
        system_difference_code: project.system_difference_code || '',
        start_date: project.start_date || format(new Date(), 'yyyy-MM-dd'),
        delivery_dates: project.delivery_dates || [],
        responsible_partner_companies: project.responsible_partner_companies || [],
        originators: project.originators || [],
        applicability: project.applicability || '',
        security_classification: project.security_classification || '01 - Unclassified',
        brex_modules: project.brex_modules || {
          project: '',
          department: '',
          division: '',
          organisation: '',
          s1000d_default: ''
        },
        country_iso_code: project.country_iso_code || 'AU',
        language_iso_code: project.language_iso_code || 'en',
        working_directory: project.working_directory || '',
        ste_technical_nouns: project.ste_technical_nouns || [],
        analytics: project.analytics
          ? { ...project.analytics, currency: project.analytics.currency || 'AUD' }
          : {
              start_date: '',
              end_date: '',
              currency: 'AUD',
              total_budget: 0,
              target_total_dms: 0,
            },
        roles_rates: project.roles_rates || [],
      });
      // Restore directory handle from IndexedDB
      if (project.id && project.working_directory) {
        getDirectoryHandle(project.id).then(async (record) => {
          if (record?.handle) {
            const ok = await verifyPermission(record.handle);
            if (ok) {
              setDirHandle(record.handle);
              setDirStatus('linked');
            } else {
              setDirStatus('error');
            }
          }
        }).catch(() => { /* IndexedDB not available */ });
      } else {
        setDirHandle(null);
        setDirStatus('none');
      }
    } else {
      // Reset form for new project
      setFormData({
        title: '',
        s1000d_version: 'Issue 5.0',
        description: '',
        capability: '00 - Product - General',
        model_ident_codes: [],
        system_difference_code: '',
        start_date: format(new Date(), 'yyyy-MM-dd'),
        delivery_dates: [],
        responsible_partner_companies: [],
        originators: [],
        applicability: '',
        security_classification: '01 - Unclassified',
        brex_modules: {
          project: '',
          department: '',
          division: '',
          organisation: '',
          s1000d_default: ''
        },
        country_iso_code: 'AU',
        language_iso_code: 'en',
        working_directory: '',
        ste_technical_nouns: [],
        analytics: {
          start_date: '',
          end_date: '',
          currency: 'AUD',
          total_budget: 0,
          target_total_dms: 0,
        },
        roles_rates: [],
      });
      setDirHandle(null);
      setDirStatus('none');
    }
    setActiveTab('basic');
  }, [project, open]);

  // Handlers for Model Ident Codes
  const addMIC = () => {
    if (newMIC.length >= 2 && newMIC.length <= 14) {
      setFormData(prev => ({
        ...prev,
        model_ident_codes: [...prev.model_ident_codes, newMIC.toUpperCase()]
      }));
      setNewMIC('');
    } else {
      toast.error('Model Ident Code must be 2-14 characters');
    }
  };

  const removeMIC = (index) => {
    setFormData(prev => ({
      ...prev,
      model_ident_codes: prev.model_ident_codes.filter((_, i) => i !== index)
    }));
  };

  // Handlers for Delivery Dates
  const addDeliveryDate = () => {
    if (newDeliveryDate) {
      setFormData(prev => ({
        ...prev,
        delivery_dates: [...prev.delivery_dates, {
          date: format(newDeliveryDate, 'yyyy-MM-dd'),
          label: newDeliveryLabel.trim(),
          verification_complete: false
        }]
      }));
      setNewDeliveryDate(null);
      setNewDeliveryLabel('');
    }
  };

  const updateDeliveryLabel = (index, label) => {
    setFormData(prev => ({
      ...prev,
      delivery_dates: prev.delivery_dates.map((d, i) =>
        i === index ? { ...d, label } : d
      )
    }));
  };

  const removeDeliveryDate = (index) => {
    setFormData(prev => ({
      ...prev,
      delivery_dates: prev.delivery_dates.filter((_, i) => i !== index)
    }));
  };

  const toggleVerification = (index) => {
    setFormData(prev => ({
      ...prev,
      delivery_dates: prev.delivery_dates.map((d, i) => 
        i === index ? { ...d, verification_complete: !d.verification_complete } : d
      )
    }));
  };

  // Handlers for Responsible Partner Companies
  const addRPC = () => {
    if (newRPC.company_name && newRPC.cage_code) {
      setFormData(prev => ({
        ...prev,
        responsible_partner_companies: [...prev.responsible_partner_companies, {
          company_name: newRPC.company_name,
          cage_code: newRPC.cage_code.toUpperCase()
        }]
      }));
      setNewRPC({ company_name: '', cage_code: '' });
    } else {
      toast.error('Please fill in both Company Name and CAGE Code');
    }
  };

  const removeRPC = (index) => {
    setFormData(prev => ({
      ...prev,
      responsible_partner_companies: prev.responsible_partner_companies.filter((_, i) => i !== index)
    }));
  };

  // Handlers for Originators
  const addOriginator = () => {
    if (newOriginator.company_name && newOriginator.cage_code) {
      setFormData(prev => ({
        ...prev,
        originators: [...prev.originators, {
          company_name: newOriginator.company_name,
          cage_code: newOriginator.cage_code.toUpperCase()
        }]
      }));
      setNewOriginator({ company_name: '', cage_code: '' });
    } else {
      toast.error('Please fill in both Company Name and CAGE Code');
    }
  };

  const removeOriginator = (index) => {
    setFormData(prev => ({
      ...prev,
      originators: prev.originators.filter((_, i) => i !== index)
    }));
  };

  // Handlers for STE Technical Nouns
  const addNoun = () => {
    if (newNoun.word.trim()) {
      setFormData(prev => ({
        ...prev,
        ste_technical_nouns: [...prev.ste_technical_nouns, { ...newNoun }]
      }));
      setNewNoun({ word: '', approved_meaning: '', example: '', non_ste_example: '' });
    } else {
      toast.error('Word is required');
    }
  };

  const removeNoun = (index) => {
    setFormData(prev => ({
      ...prev,
      ste_technical_nouns: prev.ste_technical_nouns.filter((_, i) => i !== index)
    }));
  };

  const handleSteImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSteImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('project_id', project?.id || 'new');
      const res = await axios.post(`${API}/ste-check/import-nouns`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.nouns?.length) {
        setFormData(prev => ({
          ...prev,
          ste_technical_nouns: [...prev.ste_technical_nouns, ...res.data.nouns]
        }));
        toast.success(`Imported ${res.data.count} technical nouns`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setSteImporting(false);
    e.target.value = '';
  };

  // Handlers for Analytics
  const updateAnalytics = (field, value) => {
    setFormData(prev => ({
      ...prev,
      analytics: { ...prev.analytics, [field]: value }
    }));
  };

  // Handlers for Working Directory
  const handleChooseFolder = async () => {
    try {
      const result = await pickDirectory();
      if (result) {
        setDirHandle(result.handle);
        setDirStatus('linked');
        setFormData(prev => ({ ...prev, working_directory: result.name }));
        toast.success(`Linked folder: ${result.name}`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to select folder');
      setDirStatus('error');
    }
  };

  const handleUnlinkFolder = () => {
    setDirHandle(null);
    setDirStatus('none');
    setFormData(prev => ({ ...prev, working_directory: '' }));
    if (project?.id) {
      removeDirectoryHandle(project.id).catch(() => {});
    }
    toast.info('Working directory unlinked');
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error('Project Title is required');
      setActiveTab('basic');
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = isEditing ? `${API}/projects/${project.id}` : `${API}/projects`;
      const method = isEditing ? 'put' : 'post';
      
      const res = await axios[method](endpoint, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Save directory handle to IndexedDB if one was chosen
      const projectId = isEditing ? project.id : res.data?.id;
      if (dirHandle && projectId) {
        try {
          await saveDirectoryHandle(projectId, dirHandle, formData.working_directory);
        } catch { /* IndexedDB save failed, non-critical */ }
      }

      toast.success(isEditing ? 'Project updated successfully' : 'Project created successfully');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save project');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }} className="text-2xl">
            {isEditing ? 'Edit Project' : 'Create New Project'}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <div className="px-6">
            <TabsList className="grid w-full grid-cols-7 bg-slate-100">
              <TabsTrigger value="basic" className="data-[state=active]:bg-white text-xs">
                Basic Info
              </TabsTrigger>
              <TabsTrigger value="config" className="data-[state=active]:bg-white text-xs">
                Configuration
              </TabsTrigger>
              <TabsTrigger value="org" className="data-[state=active]:bg-white text-xs">
                Organisation
              </TabsTrigger>
              <TabsTrigger value="meta" className="data-[state=active]:bg-white text-xs">
                Metadata
              </TabsTrigger>
              <TabsTrigger value="ste" className="data-[state=active]:bg-white text-xs">
                STE
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-white text-xs">
                Analytics
              </TabsTrigger>
              <TabsTrigger value="roles" className="data-[state=active]:bg-white text-xs">
                Roles & Rates
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="h-[55vh] px-6 py-4">
            {/* Basic Information Tab */}
            <TabsContent value="basic" className="mt-0 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Project Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter project title"
                  data-testid="project-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="s1000d_version">S1000D Version</Label>
                <Select 
                  value={formData.s1000d_version} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, s1000d_version: v }))}
                >
                  <SelectTrigger data-testid="s1000d-version-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {S1000D_VERSIONS.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter project description"
                  rows={3}
                  data-testid="project-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="capability">Capability</Label>
                <Select 
                  value={formData.capability} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, capability: v }))}
                >
                  <SelectTrigger data-testid="capability-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {CAPABILITY_OPTIONS.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Working Directory */}
              <div className="space-y-2">
                <Label>Working Directory</Label>
                <p className="text-xs text-slate-500">
                  Pick a folder on your computer (e.g. Documents or a SharePoint-synced folder).
                  Authored content will be saved as XML files to this location.
                </p>
                {formData.working_directory ? (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    {dirStatus === 'linked' ? (
                      <FolderCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                    ) : dirStatus === 'error' ? (
                      <FolderX className="w-5 h-5 text-amber-600 shrink-0" />
                    ) : (
                      <FolderOpen className="w-5 h-5 text-teal-600 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{formData.working_directory}</p>
                      <p className="text-xs text-slate-500">
                        {dirStatus === 'linked' ? 'Access granted' : dirStatus === 'error' ? 'Re-grant access required' : 'Saved (reconnect to verify access)'}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {isFileSystemAccessSupported() && (
                        <Button type="button" variant="outline" size="sm" onClick={handleChooseFolder} className="gap-1.5 text-xs" data-testid="change-folder-btn">
                          <FolderOpen className="w-3.5 h-3.5" /> Change
                        </Button>
                      )}
                      <Button type="button" variant="outline" size="sm" onClick={handleUnlinkFolder} className="gap-1.5 text-xs text-red-600 hover:text-red-700" data-testid="unlink-folder-btn">
                        <X className="w-3.5 h-3.5" /> Unlink
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {isFileSystemAccessSupported() ? (
                      <Button type="button" variant="outline" onClick={handleChooseFolder} className="gap-2" data-testid="choose-folder-btn">
                        <FolderOpen className="w-4 h-4" /> Choose Folder...
                      </Button>
                    ) : (
                      <p className="text-xs text-amber-600 p-2 bg-amber-50 rounded">
                        File System Access API is not supported in this browser. Please use Chrome or Edge.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Configuration Tab */}
            <TabsContent value="config" className="mt-0 space-y-6">
              {/* Model Identification Codes */}
              <div className="space-y-3">
                <Label>Model Identification Code(s)</Label>
                <p className="text-xs text-slate-500">Uppercase, 2-14 characters per code. Multiple allowed.</p>
                <div className="flex gap-2">
                  <Input
                    value={newMIC}
                    onChange={(e) => setNewMIC(e.target.value.toUpperCase())}
                    placeholder="e.g., DEMO"
                    maxLength={14}
                    className="uppercase"
                    data-testid="mic-input"
                  />
                  <Button type="button" onClick={addMIC} variant="outline" size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.model_ident_codes.map((mic, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-100 rounded-full text-sm flex items-center gap-2">
                      {mic}
                      <button onClick={() => removeMIC(i)} className="hover:text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* System Difference Code */}
              <div className="space-y-2">
                <Label htmlFor="sdc">System Difference Code</Label>
                <p className="text-xs text-slate-500">Uppercase, 1-4 characters. Single entry only.</p>
                <Input
                  id="sdc"
                  value={formData.system_difference_code}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    system_difference_code: e.target.value.toUpperCase().slice(0, 4) 
                  }))}
                  placeholder="e.g., A"
                  maxLength={4}
                  className="uppercase w-32"
                  data-testid="sdc-input"
                />
              </div>

              {/* Delivery Dates */}
              <div className="space-y-3">
                <Label>Delivery Dates</Label>
                <div className="flex gap-2 items-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newDeliveryDate ? format(newDeliveryDate, 'dd/MM/yyyy') : 'Add delivery date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" side="bottom" avoidCollisions={false}>
                      <Calendar
                        mode="single"
                        selected={newDeliveryDate}
                        onSelect={setNewDeliveryDate}
                        fromDate={new Date(1980, 0, 1)}
                        toDate={new Date(2100, 11, 31)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    value={newDeliveryLabel}
                    onChange={(e) => setNewDeliveryLabel(e.target.value)}
                    placeholder="Milestone name (e.g. IPD Delivery)"
                    className="flex-1"
                    data-testid="delivery-label-input"
                  />
                  <Button type="button" onClick={addDeliveryDate} variant="outline" size="icon" disabled={!newDeliveryDate} data-testid="delivery-add-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.delivery_dates.map((d, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium">{format(parse(d.date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')}</span>
                      <Input
                        value={d.label || ''}
                        onChange={(e) => updateDeliveryLabel(i, e.target.value)}
                        placeholder={`Delivery ${i + 1}`}
                        className="h-8 w-[200px] text-sm"
                        data-testid={`delivery-label-${i}`}
                      />
                      <div className="flex items-center gap-2 ml-auto">
                        <Checkbox
                          id={`verify-${i}`}
                          checked={d.verification_complete}
                          onCheckedChange={() => toggleVerification(i)}
                        />
                        <Label htmlFor={`verify-${i}`} className="text-sm cursor-pointer">
                          Verification Certification Complete
                        </Label>
                      </div>
                      <button onClick={() => removeDeliveryDate(i)} className="text-slate-400 hover:text-red-500">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Organisation Tab */}
            <TabsContent value="org" className="mt-0 space-y-6">
              {/* Responsible Partner Companies */}
              <div className="space-y-3">
                <Label>Responsible Partner Company</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={newRPC.company_name}
                    onChange={(e) => setNewRPC(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Company Name"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={newRPC.cage_code}
                      onChange={(e) => setNewRPC(prev => ({ ...prev, cage_code: e.target.value.toUpperCase() }))}
                      placeholder="CAGE Code"
                      className="uppercase"
                    />
                    <Button type="button" onClick={addRPC} variant="outline" size="icon">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {formData.responsible_partner_companies.map((rpc, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <span className="font-medium">{rpc.company_name}</span>
                        <span className="text-slate-500 ml-2">({rpc.cage_code})</span>
                      </div>
                      <button onClick={() => removeRPC(i)} className="text-slate-400 hover:text-red-500">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Originators */}
              <div className="space-y-3">
                <Label>Originator</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    value={newOriginator.company_name}
                    onChange={(e) => setNewOriginator(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Company Name"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={newOriginator.cage_code}
                      onChange={(e) => setNewOriginator(prev => ({ ...prev, cage_code: e.target.value.toUpperCase() }))}
                      placeholder="CAGE Code"
                      className="uppercase"
                    />
                    <Button type="button" onClick={addOriginator} variant="outline" size="icon">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {formData.originators.map((org, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <span className="font-medium">{org.company_name}</span>
                        <span className="text-slate-500 ml-2">({org.cage_code})</span>
                      </div>
                      <button onClick={() => removeOriginator(i)} className="text-slate-400 hover:text-red-500">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Metadata Tab */}
            <TabsContent value="meta" className="mt-0 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="applicability">Applicability</Label>
                <Textarea
                  id="applicability"
                  value={formData.applicability}
                  onChange={(e) => setFormData(prev => ({ ...prev, applicability: e.target.value }))}
                  placeholder="Enter applicability text"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Project Security Classification</Label>
                <Select 
                  value={formData.security_classification} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, security_classification: v }))}
                >
                  <SelectTrigger data-testid="security-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SECURITY_CLASSIFICATIONS.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Language */}
              <div className="space-y-3">
                <Label>Language</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country_iso" className="text-sm text-slate-500">Country ISO Code</Label>
                    <Select
                      value={formData.country_iso_code}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, country_iso_code: v }))}
                    >
                      <SelectTrigger data-testid="country-iso-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea className="h-[280px]">
                          {ISO_3166_COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.code} — {c.name}
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="language_iso" className="text-sm text-slate-500">Language ISO Code</Label>
                    <Select
                      value={formData.language_iso_code}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, language_iso_code: v }))}
                    >
                      <SelectTrigger data-testid="language-iso-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea className="h-[280px]">
                          {ISO_639_LANGUAGES.map((l) => (
                            <SelectItem key={l.code} value={l.code}>
                              {l.code.toUpperCase()} — {l.name}
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label>BREX Modules</Label>
                <p className="text-xs text-slate-500">Optional: Specify BREX module identifiers</p>
                <div className="grid grid-cols-1 gap-3">
                  {['project', 'department', 'division', 'organisation', 's1000d_default'].map((field) => (
                    <div key={field} className="flex items-center gap-3">
                      <Label className="w-32 text-right capitalize">{field.replace('_', ' ')}</Label>
                      <Input
                        value={formData.brex_modules?.[field] || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          brex_modules: { ...prev.brex_modules, [field]: e.target.value }
                        }))}
                        placeholder={`${field.replace('_', ' ')} BREX module`}
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* STE Tab */}
            <TabsContent value="ste" className="mt-0 space-y-6">
              {/* Explanatory text */}
              <p className="text-sm text-slate-600 leading-relaxed">
                Simplified Technical English (STE) technical nouns are project-specific approved terms. 
                Upload a spreadsheet of your technical nouns or add them manually. These nouns are used by the 
                Author STE check to validate your content against your approved terminology.
              </p>

              {/* Upload button */}
              <div>
                <label className="cursor-pointer" data-testid="ste-upload-label">
                  <input type="file" accept=".xlsx,.xls" onChange={handleSteImport} className="hidden" />
                  <span className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors">
                    {steImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Upload Technical Nouns (.xlsx)
                  </span>
                </label>
              </div>

              {/* Table or empty state */}
              {formData.ste_technical_nouns.length === 0 ? (
                <div className="text-center py-8 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                  <p className="text-sm">No technical nouns yet. Upload a spreadsheet or add a record below.</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden max-h-[28vh] overflow-y-auto">
                  <table className="w-full text-sm" data-testid="ste-nouns-table">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2.5 text-xs font-semibold text-slate-600">Word</th>
                        <th className="text-left p-2.5 text-xs font-semibold text-slate-600">Approved Meaning</th>
                        <th className="text-left p-2.5 text-xs font-semibold text-slate-600">Example</th>
                        <th className="text-left p-2.5 text-xs font-semibold text-slate-600">Non-STE Example</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {formData.ste_technical_nouns.map((noun, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="p-2.5 font-medium text-slate-700">{noun.word}</td>
                          <td className="p-2.5 text-slate-600">{noun.approved_meaning}</td>
                          <td className="p-2.5 text-slate-500">{noun.example}</td>
                          <td className="p-2.5 text-slate-500">{noun.non_ste_example}</td>
                          <td className="p-2.5">
                            <button onClick={() => removeNoun(i)} className="text-slate-400 hover:text-red-500">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* + Add Record expandable section */}
              <div>
                <Button type="button" variant="outline" size="sm" className="gap-1.5"
                  onClick={() => document.getElementById('ste-add-form')?.classList.toggle('hidden')}
                  data-testid="ste-add-record-btn">
                  <Plus className="w-3.5 h-3.5" /> Add Record
                </Button>
                <div id="ste-add-form" className="hidden mt-3 p-3 bg-slate-50 rounded-lg space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={newNoun.word} onChange={(e) => setNewNoun(prev => ({ ...prev, word: e.target.value }))} placeholder="Word" className="text-sm" />
                    <Input value={newNoun.approved_meaning} onChange={(e) => setNewNoun(prev => ({ ...prev, approved_meaning: e.target.value }))} placeholder="Approved Meaning" className="text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={newNoun.example} onChange={(e) => setNewNoun(prev => ({ ...prev, example: e.target.value }))} placeholder="STE Example" className="text-sm" />
                    <div className="flex gap-2">
                      <Input value={newNoun.non_ste_example} onChange={(e) => setNewNoun(prev => ({ ...prev, non_ste_example: e.target.value }))} placeholder="Non-STE Example" className="text-sm" />
                      <Button type="button" onClick={addNoun} size="sm" className="bg-teal-600 hover:bg-teal-700 shrink-0">Add</Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip box */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-semibold text-blue-800 mb-1">Tip</p>
                <p className="text-xs text-blue-700 leading-relaxed">
                  The Non-STE Example column is used by the STE Check tool to flag violations. 
                  When a non-STE word is found in your content, the tool will suggest using the 
                  approved Word instead. Ensure your spreadsheet columns are: Word, Approved Meaning, Example, Non-STE Example.
                </p>
              </div>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="mt-0 space-y-6">
              <div className="space-y-3">
                <Label>Schedule</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-500">Start Date</Label>
                    <Input
                      type="date"
                      value={formData.analytics?.start_date || ''}
                      onChange={(e) => updateAnalytics('start_date', e.target.value)}
                      data-testid="analytics-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-500">End Date</Label>
                    <Input
                      type="date"
                      value={formData.analytics?.end_date || ''}
                      onChange={(e) => updateAnalytics('end_date', e.target.value)}
                      data-testid="analytics-end-date"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Budget & Costs</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-500">Currency</Label>
                    <Select
                      value={formData.analytics?.currency || 'AUD'}
                      onValueChange={(v) => updateAnalytics('currency', v)}
                    >
                      <SelectTrigger data-testid="analytics-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectGroup>
                          <SelectLabel>FX-supported (live rates)</SelectLabel>
                          {CURRENCIES.filter((c) => c.fxSupported).map((c) => (
                            <SelectItem key={c.code} value={c.code}>{`${c.symbol} ${c.code} · ${c.label}`}</SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>All other currencies</SelectLabel>
                          {CURRENCIES.filter((c) => !c.fxSupported).map((c) => (
                            <SelectItem key={c.code} value={c.code}>{`${c.symbol} ${c.code} · ${c.label}`}</SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-500">Total Budget</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={
                        budgetInput !== null
                          ? budgetInput
                          : formData.analytics?.total_budget
                            ? fmtMoney(formData.analytics.total_budget, formData.analytics?.currency || 'AUD')
                            : ''
                      }
                      onFocus={() => setBudgetInput(formData.analytics?.total_budget ? String(formData.analytics.total_budget) : '')}
                      onChange={(e) => setBudgetInput(e.target.value)}
                      onBlur={() => {
                        const parsed = parseFloat((budgetInput || '').replace(/[^0-9.]/g, '')) || 0;
                        updateAnalytics('total_budget', parsed);
                        setBudgetInput(null);
                      }}
                      placeholder="0.00"
                      data-testid="analytics-budget"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-500">Target Total DMs</Label>
                    <Input
                      type="number"
                      min={0}
                      value={formData.analytics?.target_total_dms || ''}
                      onChange={(e) => updateAnalytics('target_total_dms', parseInt(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="analytics-target-dms"
                    />
                  </div>
                </div>
              </div>

              {/* Cost Summary */}
              {formData.analytics?.total_budget > 0 && formData.analytics?.target_total_dms > 0 && (
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg space-y-2">
                  <Label className="text-teal-800 font-semibold">Cost Summary</Label>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-teal-600">Cost per DM (from budget)</p>
                      <p className="text-lg font-bold text-teal-900">
                        {fmtMoney(formData.analytics.total_budget / formData.analytics.target_total_dms, formData.analytics?.currency || 'AUD')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Roles & Rates Tab */}
            <TabsContent value="roles" className="mt-0 space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Roles & Rates</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      roles_rates: [...(prev.roles_rates || []), { name: '', cost: 0 }]
                    }))}
                    data-testid="add-role-btn"
                  >
                    <Plus className="mr-1 h-4 w-4" /> Add Role
                  </Button>
                </div>
                {(!formData.roles_rates || formData.roles_rates.length === 0) ? (
                  <p className="text-sm text-slate-500 italic">No roles defined. Click "Add Role" to add one.</p>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-[1fr_120px_40px] gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">
                      <span>Role Name</span>
                      <span>Cost</span>
                      <span></span>
                    </div>
                    {formData.roles_rates.map((role, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_120px_40px] gap-2 items-center" data-testid={`role-row-${idx}`}>
                        <Input
                          value={role.name}
                          onChange={(e) => {
                            const updated = [...formData.roles_rates];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setFormData(prev => ({ ...prev, roles_rates: updated }));
                          }}
                          placeholder="e.g., Author"
                          data-testid={`role-name-${idx}`}
                        />
                        <Input
                          type="number"
                          min={0}
                          value={role.cost || ''}
                          onChange={(e) => {
                            const updated = [...formData.roles_rates];
                            updated[idx] = { ...updated[idx], cost: parseFloat(e.target.value) || 0 };
                            setFormData(prev => ({ ...prev, roles_rates: updated }));
                          }}
                          placeholder="0.00"
                          data-testid={`role-cost-${idx}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              roles_rates: prev.roles_rates.filter((_, i) => i !== idx)
                            }));
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1"
                          data-testid={`role-remove-${idx}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isLoading}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="save-project-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEditing ? 'Update Project' : 'Create Project'}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectFormModal;
